/**
 * SSML 辞書マスタ (SsmlDictionaryMaster) の画面用定義 (SBI-02)。
 *
 * 種別 (`reading` / `accent`) ごとの表示ラベルと、音声プレビュー用の入力組み立てを持つ。
 * 種別の列挙そのものは API 型 (`SSML_DICTIONARY_TYPES`) を正源とし、ここでは複製しない。
 *
 * ⚠️ 許可文字などの検証規則の正源はサーバ側 (`lambda/control-panel-api/index.py`)。
 *    画面側は入力例と注意書きを出すだけで、検証は再実装しない
 *    (二重実装すると規則変更時に食い違い、「画面では通るのに 400」になるため)。
 */
import type { SsmlDictionaryType } from '../api/types';

/** 種別ごとの表示定義 */
export interface SsmlDictionaryTypeDefinition {
  type: SsmlDictionaryType;
  /** タブ / ラジオ / 表の種別列に出す名称 */
  label: string;
  /** 「置換後」入力欄のラベル (種別で意味が変わるため切り替える) */
  replacementLabel: string;
  /** 「変換元」の入力例 (種別ごとに、その種別で登録する語の例) */
  sourceExample: string;
  /** 「置換後」の入力例 (見出しの横とプレースホルダに同じものを出す) */
  replacementExample: string;
  /** 「置換後」入力欄のプレースホルダ (入力例) */
  replacementPlaceholder: string;
  /** 「置換後」入力欄の下に出す入力ルール */
  replacementHint: string;
}

export const SSML_DICTIONARY_TYPE_DEFINITIONS: Readonly<
  Record<SsmlDictionaryType, SsmlDictionaryTypeDefinition>
> = {
  reading: {
    type: 'reading',
    label: '読み仮名のみ',
    replacementLabel: '読み (ひらがな / カタカナ)',
    sourceExample: '熊谷',
    replacementExample: 'くまがや',
    replacementPlaceholder: 'くまがや',
    replacementHint: 'ひらがな・カタカナ・長音「ー」のみ。変換元と同じ文字列は指定できません。',
  },
  accent: {
    type: 'accent',
    label: 'アクセント＋読み仮名',
    replacementLabel: 'アクセント付きの読み (全角カタカナ)',
    sourceExample: '恐れ入りますが',
    replacementExample: "オソ'レイリマスガ",
    replacementPlaceholder: "オソ'レイリマスガ",
    replacementHint:
      "全角カタカナ・長音「ー」と、アクセント記号「'」(音が下がる直前の音の後ろに付ける)。アクセント記号は任意で、複数付けても構いません。付けない場合は保存前に確認します。",
  },
  text: {
    type: 'text',
    label: 'テキスト編集',
    replacementLabel: '置換後のテキスト',
    sourceExample: '総武線各駅停車',
    replacementExample: '総武線、各駅停車',
    replacementPlaceholder: '総武線、各駅停車',
    replacementHint:
      '放送文にそのまま挿入されます。「、」を入れて間を作るなど、イントネーションを整えたいときに使います。変換元と同じ文字列は指定できません。',
  },
  ssml: {
    type: 'ssml',
    label: 'SSML構文',
    replacementLabel: '置換するSSML構文',
    sourceExample: '総武線各駅停車',
    replacementExample: '総武線<break time="300ms"/>各駅停車',
    replacementPlaceholder: '総武線<break time="300ms"/>各駅停車',
    replacementHint:
      '放送文にSSML構文のまま挿入されます。<speak> は書かないでください(放送文の全体は自動で包まれます)。タグの閉じ忘れや、単独の「&」(「&amp;」と書く)があると保存できません。変換元と同じ文字列は指定できません。',
  },
};

/** 表示順 (ラジオ)。「アクセント＋読み仮名」を先頭にする。一覧の表の並びとも揃える */
export const SSML_DICTIONARY_TYPE_ORDER: readonly SsmlDictionaryType[] = [
  'accent',
  'reading',
  'text',
  'ssml',
];

/** アクセント辞典に常時表示する注意書き */
export const ACCENT_CAUTION_MESSAGE =
  'アクセント指定は語によって効く場合と効かない場合があります。登録後は必ず「試聴」で実際の音声を確認し、確認結果を補足に残してください。';

/** アクセント核 (高 → 低に下がる直前の音の後ろに付ける記号)。サーバ側 `SSML_DICTIONARY_ACCENT_MARK` と一致させる */
export const ACCENT_MARK = "'";

/** アクセント核が無いまま保存しようとしたときの確認メッセージ (`\n` で改行) */
export const ACCENT_MARK_MISSING_CONFIRM_MESSAGE =
  "アクセント核「'」が入っていません。\nこのまま保存してよろしいですか?";

/**
 * 保存前に「アクセント核が入っていません」の確認が必要か。
 *
 * アクセント辞典で、置換後にアクセント核が 1 つも無いときだけ true。
 * アクセント核は任意 (サーバ側も必須にしていない) なので、確認はあくまで入力漏れの防止用。
 * 置換後が空のときは別の入力不備なので対象外 (保存ボタン側で弾く)。
 */
export function needsAccentMarkConfirmation(
  type: SsmlDictionaryType,
  replacement: string,
): boolean {
  if (type !== 'accent') return false;
  const value = replacement.trim();
  return value !== '' && !value.includes(ACCENT_MARK);
}

/** 種別の表示名。未知の値 (将来の種別追加など) はそのまま返す */
export function dictionaryTypeLabel(type: string): string {
  return SSML_DICTIONARY_TYPE_DEFINITIONS[type as SsmlDictionaryType]?.label ?? type;
}

/** XML の特殊文字をエスケープする (テキストノード・属性値の両方で安全) */
function escapeXml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** `POST /audio-preview` に渡す入力 (`text` か `ssml` のどちらか) */
export type DictionaryPreviewInput =
  | { text: string; isSsml: false }
  | { ssml: string; isSsml: true };

/**
 * 辞書項目を試聴するための `POST /audio-preview` 入力を組み立てる。
 *
 * - `reading`: 読みそのもの (置換後) を読み上げる。本番でも置換後の文字列がそのまま
 *   放送文へ挿入される想定のため、置換後を読ませれば聞こえ方が一致する。
 * - `text`: `reading` と同じ。置換後のテキストをそのまま読み上げる。
 * - `ssml`: 置換後の SSML 断片を `<speak>` で包んだ SSML。本番でも断片がそのまま挿入される。
 * - `accent`: `<phoneme alphabet="x-amazon-pron-kana" ph="置換後">変換元</phoneme>` を
 *   `<speak>` で包んだ SSML。本番でも同じ形で挿入される想定。
 *
 * 入力が空のときは `null` (試聴不可)。
 */
export function buildDictionaryPreviewInput(
  type: SsmlDictionaryType,
  sourceText: string,
  replacement: string,
): DictionaryPreviewInput | null {
  const source = sourceText.trim();
  const reading = replacement.trim();
  if (reading === '') return null;

  if (type === 'ssml') {
    // 置換後は SSML の断片。本番の挿入先と同じく `<speak>` の内側に置く (エスケープしない)。
    return { ssml: `<speak>${reading}</speak>`, isSsml: true };
  }
  if (type === 'accent') {
    if (source === '') return null;
    return {
      ssml: `<speak><phoneme alphabet="x-amazon-pron-kana" ph="${escapeXml(reading)}">${escapeXml(source)}</phoneme></speak>`,
      isSsml: true,
    };
  }
  return { text: reading, isSsml: false };
}
